import Head from "next/head"
import {NextPage, GetServerSideProps} from "next"
import {MainLayout} from "../../src/components/main-layout";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {Grid, Container, Paper, Fade, Button, Typography, Box} from "@mui/material";
import {differenceInYears, parse, parseISO} from "date-fns";
import {useTranslation} from "next-i18next";
import {useAuth} from "../../src/hooks/use-auth";
import {useEffect, useState} from "react";
import {isTouchDevice} from "../../src/lib/util";
import {getAuth} from "firebase/auth";
import {dateFormat} from "../../src/lib/date-format";

import PencilIcon from "@mui/icons-material/Edit"

import IconRoom from "@mui/icons-material/Room";
import IconCalendar from "@mui/icons-material/InsertInvitationOutlined";
import IconGender from "mdi-material-ui/GenderMaleFemale";
import IconCake from "@mui/icons-material/CakeOutlined";

import {convertDocToUser, getUserCertifications, getUser} from "seacrush-core/dist/service/user-service";

import {useSelector, wrapper} from "../../src/store";
import {setCurrentProfile} from "../../src/slices/user-session-slice";
import {ProfileCoverImage} from "../../src/components/profile/profile-cover-image";
import {ProfilePhoto} from "../../src/components/profile/profile-photo";
import {FormattedCertification} from "../../src/components/formatted-certification";
import {DiveNumber} from "../../src/components/dive-number";
import {ProfileFriends} from "../../src/components/profile/profile-friends";
import {NeedsAcceptanceCard} from "../../src/components/profile/needs-acceptance-card";
import {HasAcceptanceCard} from "../../src/components/profile/has-acceptance-card";
import {BioCard} from "../../src/components/profile/bio-card";
import {InterestedTripsCard} from "../../src/components/profile/interested-trips-card";
import {CertificationsCard} from "../../src/components/profile/certifications-card";
import {ProfileUpdateDialog} from "../../src/components/profile/profile-update-dialog";
import {UserInterface} from "seacrush-core";
import {ProfilePageSignIn} from "../../src/components/profile/profile-page-sign-in";
import {NextSeo} from "next-seo";
import {encodePayload} from "../../src/utils/payload";
import {ProfileShareImagePayload} from "../api/profile-share-image";
import useWidth from "../../src/hooks/use-width";


const leftSideIcon: any = {
    marginLeft: 8,
    marginRight: 8,
    verticalAlign: "middle"
}


// export const config =  {
//     runtime: "experimental-edge"
// }

interface ProfilePageProps {
    shareImagePayload: string
}

const ProfilePage: NextPage<ProfilePageProps> = ({shareImagePayload}) => {

    const {currentProfile} = useSelector((state) => state.userSession)

    const {
        accepted,
        username,
        uid,
        photoUrl,
        photoHash,
        homeCountryCode,
        joined,
        gender,
        birthday,
        currentLocation,
        firstName,
        lastName,
        certBody,
        certLevel,
        numberOfDives,
        disableWebIndexing
    } = currentProfile

    const {t, i18n} = useTranslation()



    const {isAuthenticated, isAdmin, user} = useAuth()


    //const dispatch = useDispatch();

    const isAllowedToEdit = user?.uid === uid || isAdmin;

    const fullScreen = useWidth() === "xs"


    const [showPen, setShowPen] = useState(
        isTouchDevice() && isAllowedToEdit
    );

    const [showUpdateDialog, setShowUpdateDialog] = useState(false);


    useEffect(() => {
        const unsubscribe = getAuth().onIdTokenChanged(async user => {
            // dispatch(fetchUserProfile(embeddedProfile.uid));
        });

        return function cleanup() {
            unsubscribe();
        };
    }, []);

    const handleMouseEnter = () => {
        showPenIfOwner();
    };

    const handleMouseLeave = () => {
        hidePenUnlessTouch();
    };

    const hidePenUnlessTouch = () => {
        if (!isTouchDevice()) {
            setShowPen(false);
        }
    };

    const showPenIfOwner = () => {
        if (isAllowedToEdit) {
            setShowPen(true);
        }
    };

    const updateProfileClicked = () => {
        setShowUpdateDialog(true);
    };

    const profileUpdateFinished = () => {
        setShowUpdateDialog(false);
    };


    // The person's birthday. This is a fixed format string, like MM/DD/YYYY. However, people can control who can see the year they were born separately from the month and day so this string can be only the year (YYYY) or the month + day (MM/DD)
    // https://developers.facebook.com/docs/graph-api/reference/user/

    let age = null;

    try {
        //could be any of these:
        //const _birthday = "11/06"
        //const _birthday = "11/06/1980"
        //const _birthday = "1980"

        const _birthday = birthday;

        if (!!_birthday && _birthday.length === 4) {
            age =
                "~" +
                differenceInYears(new Date(), parse(_birthday, "yyyy", new Date()));
        } else if (!!_birthday && _birthday.length === 10) {
            age = differenceInYears(
                new Date(),
                parse(_birthday, "MM/dd/yyyy", new Date())
            );
        }
    } catch (e) {
        console.error(e);
    }

    const joinedFormatted = joined ? dateFormat(parseISO(joined), "MMM yyyy") : "";

    return (
        <div>
            <Head>
                <title>{`${firstName} ${lastName} - ${certLevel} | SeaCrush`}</title>
                <meta name='robots' content={`${disableWebIndexing ? "no" : ""}index,follow`}/>
                <meta property='og:type' content='profile'/>
            </Head>

            <NextSeo
                facebook={{
                    appId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID as string
                }}
                openGraph={{
                    url: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}/${i18n.language}/u/${username}`,
                    type: "profile",
                    profile: {
                        firstName: firstName,
                        lastName: lastName,
                        username: username
                    },
                    title: t(accepted ? "share-profile-title-accepted" : "share-profile-title-pending", {
                        firstName: firstName,
                    }),
                    description: t(accepted ? "share-profile-description-accepted" : "share-profile-description-pending", {
                        firstName: firstName,
                    }),
                    images: [
                        {
                            width: 1280,
                            height: 668,
                            type: "image/png",
                            // DO NOT pass more than one url param here because of react limitions. see payload.ts
                            url: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}/api/profile-share-image?payload=${shareImagePayload}`
                        }
                    ]
                }}
                twitter={{
                    cardType: "summary_large_image"
                }}
            />
            <section>

                <Container maxWidth={"lg"} sx={{
                    mt: {
                        xs: 3,
                        sm: 0
                    },
                    px: {
                        xs: 0,
                        sm: 1
                    }
                }}>

                    <Paper elevation={4} sx={{mx: {xs: 0, md: 2}}}>
                        <ProfileCoverImage/>

                        <div
                            onMouseEnter={(e) => handleMouseEnter()}
                            onMouseLeave={(e) => handleMouseLeave()}
                        >
                            <Grid container direction={"row"}
                                  sx={{
                                      position: "relative",
                                      justifyContent: {
                                          md: "left",
                                          xs: "center"
                                      },
                                      pb: 2
                                  }}
                            >
                                <Grid item
                                      xs={12}
                                      md={3}
                                      sx={{position: "relative", py: 2}}
                                >
                                    <ProfilePhoto/>
                                    <div style={{width: 168, height: 116}}>&nbsp;</div>
                                </Grid>
                                <Grid item md={"auto"}
                                      sx={{
                                          position: "initial",
                                          pb: 1,
                                          textAlign: {
                                              xs: "center",
                                              md: "left"
                                          }
                                      }}
                                      alignItems={"center"}>
                                    <Typography
                                        component={"h1"}
                                        variant={"h3"}
                                        sx={{
                                            color:"#454545",
                                            mt:3,
                                            textAlign: "center",
                                            display: {
                                                xs: "block",
                                                sm: "inline-block"
                                            },

                                        }}>
                                        <img
                                            src={`/images/flags/${homeCountryCode}.png`}
                                            style={{
                                                ...(!isAuthenticated && {
                                                    filter: "grayscale(100%) blur(1px)"
                                                }),
                                                width: 32,
                                                marginRight: 6,
                                                verticalAlign: "middle",
                                                paddingBottom: 4
                                            }}
                                        />
                                        {firstName} {lastName}
                                    </Typography>
                                    <FormattedCertification
                                        certBody={certBody}
                                        certLevel={certLevel}
                                    />
                                    <DiveNumber
                                        numberOfDives={numberOfDives}
                                    />

                                    <Fade mountOnEnter unmountOnExit in={
                                        isAuthenticated &&
                                        (showPen || fullScreen)}>
                                        <Button
                                            sx={{
                                                mt: {
                                                    lg: 2,
                                                    xs: 1
                                                },
                                                position: {
                                                    md: "absolute"
                                                },
                                                top: {
                                                    md: 0
                                                },
                                                right: {
                                                    md: 16
                                                }
                                            }}
                                            variant='text'
                                            color='primary'
                                            onClick={updateProfileClicked}
                                            endIcon={<PencilIcon/>}
                                        >
                                            {t("update-profile")}{" "}

                                        </Button>
                                    </Fade>

                                    <ProfileUpdateDialog
                                        open={showUpdateDialog}
                                        onFinished={profileUpdateFinished}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Paper>


                    <Grid container spacing={0}>
                        <Grid item xs={12} md={4} sx={{pt: {xs: 3, md: 5}, px: {xs: 1, md: 2},}}>
                            {!!currentLocation && (
                                <Box pb={1}>
                                    <IconRoom style={leftSideIcon} />
                                    {currentLocation}
                                </Box>
                            )}
                            {!!gender && (
                                <Box pb={1}>
                                    <IconGender style={leftSideIcon}/>
                                    {
                                        t(gender)
                                    }
                                </Box>
                            )}
                            {!!age && (
                                <Box pb={1}>
                                    <IconCake style={leftSideIcon}/>
                                    {age} {t("years")}
                                </Box>
                            )}
                            <div>
                                <IconCalendar style={leftSideIcon}/>
                                {t("joined")} {joinedFormatted}
                                <p/>
                            </div>

                            <ProfileFriends/>
                        </Grid>
                        <Grid item xs={12} md={8} sx={{py: 2, px: {xs: 1, md: 2}}}>
                            {isAuthenticated ? (
                                <div>
                                    {!accepted && <NeedsAcceptanceCard/>}

                                    {accepted && <HasAcceptanceCard/>}

                                    <BioCard/>

                                    <CertificationsCard/>

                                    <InterestedTripsCard/>

                                    {/*{isAdmin && <AdminDashboard uid={uid} />}*/}
                                    {/*{(isOwnProfile || isAdmin) &&*/}
                                    {/*<PrivateDashboard uid={uid} username={username}/>*/}
                                    {/*}*/}
                                </div>
                            ) : (
                                <ProfilePageSignIn/>
                            )}
                        </Grid>
                    </Grid>

                </Container>
            </section>
        </div>
    )
}


ProfilePage.getLayout = (page) => (
    <MainLayout>
        {page}
    </MainLayout>
);

export default ProfilePage

// export const getStaticPaths: GetStaticPaths = async (context) => {
//
//     const locales = context.locales?.filter((val) => val !== 'default') || []
//
//     // const locales = context.locales || []
//
//     const usersSnap = await firestore.collection("users").get()
//
//
//     const paths = []
//     for (const userDoc of usersSnap.docs) {
//         for (const l of locales) {
//             const username = userDoc.data().usernameLowercase
//             if (username) {
//                 paths.push({
//                     params: {
//                         username,
//                     },
//                     locale: l
//                 })
//             }
//
//         }
//     }
//
//
//     return {
//         paths,
//         fallback: false
//     }
// }

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(store => async ({params, locale}) => {
    if (locale === "default") {
        return {notFound: true};
    }

    const userDoc = await getUser(params?.username as string);

    const profileData: UserInterface = convertDocToUser(userDoc)

    profileData.certifications = await getUserCertifications(profileData.uid)

    await store.dispatch(setCurrentProfile(profileData))

    const cardName = profileData.firstName + " " + profileData.lastName?.charAt(0).toUpperCase() + ".";

    const shareImagePayload = encodePayload<ProfileShareImagePayload>({
        lng: locale || "en",
        cardName,
        certLevel: profileData.certLevel,
        photoUrl: profileData.photoUrl,
        photoHash: profileData.photoHash,
        homeCountryCode: profileData.homeCountryCode,
        accepted: profileData.accepted
    })


    // const tripData = await getTripData(params?.country as string, params?.slug as string, locale)
    return {
        props: {
            shareImagePayload,
            ...(await serverSideTranslations(locale as string, ["frontend"])),
        }
    };
})
